'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  id: string;
  number: number;
  icon: string;
  title: string;
  description: string;
  details: string[];
}

const processSteps: ProcessStep[] = [
  {
    id: 'order',
    number: 1,
    icon: 'DocumentTextIcon',
    title: 'bestill henting',
    description: 'Fyll ut skjema og avtal tidspunkt',
    details: [
      'Helt kostnadsfritt',
      'Ingen forpliktelser',
      'Velg tidspunkt som passer deg',
      'Diskret og profesjonelt',
    ],
  },
  {
    id: 'ship',
    number: 2,
    icon: 'TruckIcon',
    title: 'Vi kommer hjem til deg',
    description: 'Våre eksperter møter deg hjemme',
    details: [
      'Avtalt tid og sted',
      'Profesjonell gjennomføring',
      'Trygg og diskret',
      'Du bestemmer selv',
    ],
  },
  {
    id: 'evaluation',
    number: 3,
    icon: 'MagnifyingGlassIcon',
    title: 'Gullet vurderes hos deg',
    description: 'Vurdering og pristilbud på stedet',
    details: [
      'Profesjonell vurdering',
      'Nøyaktig vektmåling',
      'Karattest og kvalitetskontroll',
      'Pristilbud umiddelbart',
    ],
  },
  {
    id: 'payment',
    number: 4,
    icon: 'BanknotesIcon',
    title: 'Oppgjør på stedet',
    description: 'Du får pengene med en gang',
    details: [
      'Direkte oppgjør',
      'Vipps eller bankoverføring',
      'Ingen venting',
      'Du bestemmer selv om du vil selge',
    ],
  },
];

const ProcessSection = () => {
  const [activeStep, setActiveStep] = useState<string | null>(null);

  return (
    <section id="prosess" className="bg-background py-12 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
            Slik fungerer det
          </h2>
          <p className="mt-4 font-body text-lg text-muted-foreground lg:text-xl">
            Fire enkle steg til trygg betaling for gullet ditt
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-4">
          {processSteps.map((step, index) => (
            <div
              key={step.id}
              className="group relative"
              onMouseEnter={() => setActiveStep(step.id)}
              onMouseLeave={() => setActiveStep(null)}
            >
              <div className="relative rounded-lg bg-card p-6 shadow-md transition-all duration-300 hover:shadow-xl lg:p-8">
                <div className="absolute -top-4 left-6 flex h-12 w-12 items-center justify-center rounded-full bg-primary font-headline text-xl font-bold text-primary-foreground shadow-md">
                  {step.number}
                </div>

                <div className="mt-6 flex flex-col items-center text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 transition-colors duration-300 group-hover:bg-primary">
                    <Icon
                      name={step.icon as any}
                      size={32}
                      variant="outline"
                      className="text-primary transition-colors duration-300 group-hover:text-primary-foreground"
                    />
                  </div>

                  <h3 className="font-headline text-xl font-semibold text-foreground lg:text-2xl">
                    {step.title}
                  </h3>
                  <p className="mt-3 font-body text-sm text-muted-foreground lg:text-base">
                    {step.description}
                  </p>

                  {activeStep === step.id && (
                    <div className="mt-4 space-y-2 text-left">
                      {step.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <Icon name="CheckCircleIcon" size={16} variant="solid" className="mt-0.5 text-success" />
                          <span className="font-body text-xs text-muted-foreground">{detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {index < processSteps.length - 1 && (
                  <div className="absolute -right-4 top-1/2 hidden h-0.5 w-8 -translate-y-1/2 bg-border lg:block"></div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-lg bg-success/10 p-6 lg:p-8">
          <div className="flex flex-col items-center space-y-4 text-center lg:flex-row lg:space-x-6 lg:space-y-0 lg:text-left">
            <Icon name="ShieldCheckIcon" size={48} variant="solid" className="text-success" />
            <div className="flex-1">
              <h3 className="font-headline text-xl font-semibold text-foreground lg:text-2xl">
                Trygghet i hvert steg
              </h3>
              <p className="mt-2 font-body text-base text-muted-foreground">
                Vi er med deg hele veien. Du bestemmer selv om du vil selge – ingen forpliktelser.
              </p>
            </div>
            <a
              href="#priskalkulator"
              className="flex items-center space-x-2 rounded-lg bg-accent px-6 py-3 font-cta text-base font-semibold text-accent-foreground shadow-cta transition-all duration-250 hover:bg-accent/90"
            >
              <span>Kom i gang nå</span>
              <Icon name="ArrowRightIcon" size={18} variant="outline" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;